import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '@/app/shared/services/notification.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Register } from '@/app/shared/models/register-model';

export interface OtpDialogData {
  email: string;
  mode: 'registration' | 'login';
  personData?: Register;
}

@Component({
  selector: 'app-otp-verification',
  imports: [CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatIconModule,
    TranslatePipe
  ],
  templateUrl: './otp-verification.html',
  styleUrl: './otp-verification.scss'
})
export class OtpVerification implements OnInit, OnDestroy {

  form!: FormGroup;

  mode: 'registration' | 'login' = 'login';
  email = '';
  personData?: Register;

  resendCooldown = 0;
  private countdownTimer: ReturnType<typeof setInterval> | undefined;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<OtpVerification>,
    @Inject(MAT_DIALOG_DATA) public data: OtpDialogData,
    private authService: AuthService,
    private notificacionService: NotificationService,
    protected ngxLoaderService: NgxUiLoaderService,
    private translate: TranslateService
  ) {
    if (this.data.email) {
      this.email = this.data.email;
    }
    this.mode = this.data.mode || 'login';
    this.personData = this.data.personData;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      otp: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  ngOnDestroy(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }

  cancelar() {
    this.dialogRef.close();
  }

  verificar(): void {
    if (this.form.invalid) {
      return;
    }

    const otp = this.form.get('otp')?.value;

    let request$;
    if (this.mode === 'registration' && this.personData) {
      request$ = this.authService.validarOtp(otp, this.personData);
    } else {
      request$ = this.authService.validarOtpLogin(otp, this.email);
    }

    this.ngxLoaderService.start();
    request$.subscribe({
      next: (res) => {
        this.ngxLoaderService.stop();
        if (res && res.result) {
          this.notificacionService.notificationSuccess(
            this.translate.instant('OTP_VERIFICATION.SUCCESS_MESSAGE'),
            this.translate.instant('COMMON.SUCCESS')
          );
        }
        this.dialogRef.close({ success: true });
      },
      error: (err) => {
        this.ngxLoaderService.stop();
        this.notificacionService.notificationError(
          this.translate.instant('OTP_VERIFICATION.INVALID_CODE'),
          this.translate.instant('COMMON.ERROR')
        );
      },
    });
  }

  reenviar(): void {
    this.ngxLoaderService.start();
    this.authService.resendOtp(this.email).subscribe({
      next: (res) => {
        this.ngxLoaderService.stop();
        this.notificacionService.notificationSuccess(
          this.translate.instant('OTP_VERIFICATION.RESEND_SUCCESS'),
          this.translate.instant('COMMON.SUCCESS')
        );
        this.startCooldown();
      },
      error: (err) => {
        this.ngxLoaderService.stop();
        this.notificacionService.notificationError(
          this.translate.instant('ERRORS.SORRY_ERROR'),
          this.translate.instant('COMMON.ERROR')
        );
      },
    });
  }

  private startCooldown(): void {
    this.resendCooldown = 60;
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    this.countdownTimer = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = undefined;
      }
    }, 1000);
  }
}